import { useState } from "react";
import {
  useClinicScheduleOverrides,
  useCreateClinicScheduleOverride,
  useUpdateClinicScheduleOverride,
  useDeleteClinicScheduleOverride,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import { canManageClinicScheduleOverrides } from "@utils";
import { Badge, showSuccess, ConfirmModal } from "@common";
import DateRangeInputs from "./DateRangeInputs";
import {
  SCHEDULE_TIMES,
  formatScheduleDate,
  emptyDateRange,
  extractApiError,
} from "./scheduleUtils";

function emptyDraft() {
  return {
    date_ranges: [emptyDateRange()],
    is_open: true,
    start_time: "",
    end_time: "",
    reason: "",
  };
}

const selectCls =
  "rounded-md border border-palette-gray-divider bg-white px-2 py-1.5 text-xs text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-indicator";

export default function ClinicScheduleOverrideSection({ clinicId }) {
  const { data, isLoading } = useClinicScheduleOverrides({ clinic: clinicId });
  const overrides = data?.results ?? [];

  // Custom clinic hours are management-only (roles 1-3). Practitioner Lead can
  // manage closure days (see ClinicVacationSection) but not overrides, so it
  // reads this section without write buttons rather than 403ing on submit.
  const { user } = useAuth();
  const canEdit = canManageClinicScheduleOverrides(user);

  const createMut = useCreateClinicScheduleOverride();
  const updateMut = useUpdateClinicScheduleOverride();
  const deleteMut = useDeleteClinicScheduleOverride();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  function startAdd() {
    setDraft(emptyDraft());
    setEditingId(null);
    setAdding(true);
    setError("");
  }

  function startEdit(ov) {
    setDraft({
      date_ranges:
        ov.date_ranges.length > 0
          ? ov.date_ranges.map((r) => ({ start_date: r.start_date, end_date: r.end_date }))
          : [emptyDateRange()],
      is_open: ov.is_open,
      start_time: ov.start_time ?? "",
      end_time: ov.end_time ?? "",
      reason: ov.reason ?? "",
    });
    setEditingId(ov.id);
    setAdding(true);
    setError("");
  }

  function cancelEdit() {
    setAdding(false);
    setEditingId(null);
    setDraft(emptyDraft());
    setError("");
  }

  function updateRange(idx, field, value) {
    setDraft((prev) => ({
      ...prev,
      date_ranges: prev.date_ranges.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  }

  async function handleSave() {
    setError("");
    const validRanges = draft.date_ranges.filter((r) => r.start_date && r.end_date);
    if (validRanges.length === 0) {
      setError("At least one complete date range is required.");
      return;
    }
    if (draft.is_open && (!draft.start_time || !draft.end_time)) {
      setError("Start time and end time are required for an open override.");
      return;
    }
    const payload = {
      clinic: clinicId,
      date_ranges: validRanges,
      is_open: draft.is_open,
      start_time: draft.is_open ? draft.start_time : "",
      end_time: draft.is_open ? draft.end_time : "",
      reason: draft.reason.trim() || null,
    };
    try {
      if (editingId) {
        await updateMut.mutateAsync({ id: editingId, data: payload });
        showSuccess("Override updated successfully.");
      } else {
        await createMut.mutateAsync(payload);
        showSuccess("Override saved successfully.");
      }
      cancelEdit();
    } catch (e) {
      setError(extractApiError(e, "Failed to save override."));
    }
  }

  async function handleDelete() {
    try {
      await deleteMut.mutateAsync(deleteConfirm);
      showSuccess("Override deleted.");
    } catch {
      // no-op
    } finally {
      setDeleteConfirm(null);
    }
  }

  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-palette-slate-heading">Schedule Overrides</h3>
        {canEdit && !adding && (
          <button
            type="button"
            onClick={startAdd}
            className="text-xs font-medium text-palette-blue-badge hover:text-palette-blue-text"
          >
            + Add
          </button>
        )}
      </div>

      {isLoading && <p className="text-xs text-palette-gray-placeholder">Loading...</p>}
      {!isLoading && overrides.length === 0 && !adding && (
        <p className="text-xs text-palette-gray-placeholder">No overrides set.</p>
      )}

      <div className="space-y-2">
        {overrides.map((ov) => (
          <div
            key={ov.id}
            className="overflow-hidden rounded-xl border border-palette-gray-divider bg-white"
          >
            <div className="flex items-start justify-between px-4 py-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge color={ov.is_open ? "emerald" : "red"}>
                    {ov.is_open ? "Open" : "Closed"}
                  </Badge>
                  {ov.is_open && ov.start_time && ov.end_time && (
                    <span className="text-xs text-palette-slate-badge">
                      {ov.start_time} – {ov.end_time}
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {ov.date_ranges.map((r, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-palette-blue-indicator shrink-0" />
                      <span className="text-sm text-palette-slate-heading">
                        {formatScheduleDate(r.start_date)} – {formatScheduleDate(r.end_date)}
                      </span>
                    </div>
                  ))}
                </div>
                {ov.reason && (
                  <p className="mt-1.5 text-xs text-palette-gray-secondary italic">{ov.reason}</p>
                )}
              </div>
              {canEdit && (
                <div className="ml-3 flex shrink-0 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => startEdit(ov)}
                    className="text-xs text-palette-blue-label hover:text-palette-blue-heading"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(ov.id)}
                    disabled={deleteMut.isPending}
                    className="text-xs text-palette-red-indicator hover:text-palette-red-badge disabled:opacity-40"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {canEdit && adding && (
        <div className="mt-3 rounded-xl border border-palette-blue-border bg-palette-blue-soft/40 p-4">
          <div className="mb-3 text-xs font-semibold text-palette-blue-heading">
            {editingId ? "Edit override" : "New override"}
          </div>
          {error && <p className="mb-2 text-xs text-palette-red-label">{error}</p>}

          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs text-palette-gray-secondary">Type:</span>
            {[true, false].map((val) => (
              <button
                key={String(val)}
                type="button"
                onClick={() => setDraft((p) => ({ ...p, is_open: val }))}
                className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
                  draft.is_open === val
                    ? val
                      ? "bg-palette-emerald-badge text-white border-palette-emerald-badge"
                      : "bg-palette-red-label text-white border-palette-red-label"
                    : "bg-white text-palette-gray-secondary border-palette-gray-divider hover:border-palette-gray-border"
                }`}
              >
                {val ? "Open (custom hours)" : "Closed"}
              </button>
            ))}
          </div>

          {draft.is_open && (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs text-palette-gray-secondary">Hours:</span>
              <select
                value={draft.start_time}
                onChange={(e) => setDraft((p) => ({ ...p, start_time: e.target.value }))}
                className={selectCls}
              >
                <option value="">Start</option>
                {SCHEDULE_TIMES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <span className="text-xs text-palette-gray-placeholder">–</span>
              <select
                value={draft.end_time}
                onChange={(e) => setDraft((p) => ({ ...p, end_time: e.target.value }))}
                className={selectCls}
              >
                <option value="">End</option>
                {SCHEDULE_TIMES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}

          <DateRangeInputs
            ranges={draft.date_ranges}
            onChange={updateRange}
            onAdd={() =>
              setDraft((p) => ({ ...p, date_ranges: [...p.date_ranges, emptyDateRange()] }))
            }
            onRemove={(idx) =>
              setDraft((p) => ({ ...p, date_ranges: p.date_ranges.filter((_, i) => i !== idx) }))
            }
          />

          <textarea
            value={draft.reason}
            onChange={(e) => setDraft((p) => ({ ...p, reason: e.target.value }))}
            placeholder="Reason (optional)"
            rows={2}
            className="mt-3 w-full resize-none rounded-md border border-palette-gray-divider bg-white px-3 py-2 text-xs text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-indicator"
          />

          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs font-medium text-palette-gray-secondary hover:text-palette-gray-heading"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-lg bg-palette-blue-badge px-3 py-1.5 text-xs font-medium text-white hover:bg-palette-blue-heading disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete override?"
        message="This schedule override will be permanently removed and cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMut.isPending}
      />
    </div>
  );
}
